import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import LanguageSwitcher from "@/features/driver/components/LanguageSwitcher";
import { clearDriverToken } from "@/features/driver/api/driverApi";
import { DriverLanguage } from "@/features/driver/i18n/translations";
import { useDriverLanguage } from "@/features/driver/i18n/useDriverLanguage";

/** Top-right hamburger menu for post-login driver pages: language switcher + sign out. */
export default function DriverMenu() {
  const navigate = useNavigate();
  const { language, setLanguage, t, availableLanguages } = useDriverLanguage();
  const [open, setOpen] = useState(false);

  const selectLanguage = (lang: DriverLanguage) => {
    setLanguage(lang);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Menu"
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-white/30 text-white transition-colors hover:bg-black/10"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        {availableLanguages.length > 1 && (
          <>
            <span className="block px-2 pb-1.5 pt-1 text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Language
            </span>
            <LanguageSwitcher
              value={language}
              onChange={selectLanguage}
              languages={availableLanguages}
              variant="onLight"
              className="flex-wrap px-1 pb-2"
            />
            <div className="my-1 h-px bg-neutral-200" />
          </>
        )}
        <button
          type="button"
          onClick={() => {
            clearDriverToken();
            navigate("/driver/login");
          }}
          className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" /> {t("signOutAria")}
        </button>
      </PopoverContent>
    </Popover>
  );
}
